import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAdapter } from '../api/authAdapter';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import SocialFooter from '../components/SocialFooter';
import SponsorModal from '../components/SponsorModal';
import { useLocale } from '../context/LocaleContext';

export default function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const { locale, toggleLocale, t } = useLocale();

  const [providers, setProviders] = useState(null);
  
  // 'login' | 'register' | 'forgot'
  const [view, setView] = useState('login');
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSponsorOpen, setIsSponsorOpen] = useState(false);

  useEffect(() => {
    authAdapter.getProviders().then(setProviders);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setIsLoading(true);

    try {
      if (view === 'login') {
        await login(email, password);
        navigate('/');
      } else if (view === 'register') {
        await register(email, password);
        navigate('/');
      } else if (view === 'forgot') {
        // Build redirect URL matching HashRouter configuration
        const redirectTo = window.location.origin + window.location.pathname + '#/reset-password';
        await authAdapter.requestPasswordReset(email, redirectTo);
        setSuccessMsg(t('login.resetSuccess'));
        // Do not redirect immediately, let them read the success message
      }
    } catch (err) {
      console.error("Auth Error:", err);
      const msg = err.message || '';
      let errorKey = 'errors.default';
      
      // 智慧辨識常見錯誤內容，避免被句號切斷導致翻譯失敗
      const msgLower = msg.toLowerCase();
      if (msgLower.includes('too many requests') || msgLower.includes('rate limit')) {
        errorKey = 'errors.tooManyRequests';
      } else if (msgLower.includes('already exists')) {
        errorKey = 'errors.userExists';
      } else if (msgLower.includes('least 8 characters')) {
        errorKey = 'errors.passwordLength';
      } else if (msgLower.includes('invalid login') || msgLower.includes('invalid email') || msgLower.includes('invalid credentials') || msgLower.includes('invalid login credentials')) {
        errorKey = 'errors.invalidCredentials';
      } else if (msg.startsWith('errors.')) {
        errorKey = msg;
      }
      
      if (errorKey !== 'errors.default' || !msg) {
        setError(t(errorKey));
      } else {
        setError(msg); // 無法辨識的錯誤，直接顯示原始訊息
      }
    } finally {
      setIsLoading(false);
    }
  };

  const switchView = (newView) => {
    setView(newView);
    setError('');
    setSuccessMsg('');
  };

  return (
    <div className="min-h-screen flex flex-col p-4 relative overflow-hidden">
      <button 
        onClick={toggleLocale}
        className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-700 backdrop-blur-md text-gray-600 dark:text-gray-300 transition-all font-bold text-sm shadow-sm z-50 flex items-center justify-center border border-gray-100 dark:border-slate-700"
      >
        {locale === 'zh-TW' ? 'EN' : '中'}
      </button>

      {/* 裝飾背景 */}
      <div className="fixed top-10 left-10 w-32 h-32 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
      <div className="fixed top-10 right-10 w-32 h-32 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
      <div className="fixed bottom-10 left-20 w-32 h-32 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>

      <div className="flex-1 flex items-center justify-center z-10 w-full mt-10">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-[2.5rem] shadow-2xl p-8 border-4 border-white dark:border-slate-800 relative"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={view}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.2 }}
            >
              {view === 'forgot' ? (
                <div className="text-center mb-8">
                  <div className="mx-auto w-20 h-20 bg-gradient-to-br from-purple-400 to-indigo-400 rounded-[1.5rem] flex items-center justify-center shadow-lg shadow-purple-200 dark:shadow-none rotate-3 mb-6">
                    <SafeIcon name="Key" className="w-10 h-10 text-white" />
                  </div>
                  <h2 className="text-3xl font-black text-gray-800 dark:text-white tracking-tight">{t('login.resetTitle')}</h2>
                  <p className="text-gray-500 dark:text-gray-400 mt-2 font-medium">{t('login.resetDesc')}</p>
                </div>
              ) : (
                <div className="text-center mb-8">
                  <div className="mx-auto w-20 h-20 bg-gradient-to-br from-pink-400 to-rose-400 rounded-[1.5rem] flex items-center justify-center shadow-lg shadow-pink-200 dark:shadow-none rotate-3 mb-6">
                    <SafeIcon name="Heart" className="w-10 h-10 text-white" />
                  </div>
                  <h2 className="text-3xl font-black text-gray-800 dark:text-white tracking-tight">{t('login.welcome')}</h2>
                  <p className="text-gray-500 dark:text-gray-400 mt-2 font-medium">{t('login.subtitle')}</p>
                </div>
              )}

              {!providers ? (
                <div className="flex justify-center p-8">
                  <SafeIcon name="Loader" className="w-8 h-8 animate-spin text-pink-400" />
                </div>
              ) : (
                <>
                  {providers.email ? (
                    <form className="space-y-5" onSubmit={handleSubmit}>
                      <div>
                        <input 
                          type="email" 
                          required 
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full px-5 py-4 bg-gray-50 dark:bg-slate-800 border-2 border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-4 focus:ring-pink-100 dark:focus:ring-pink-900/30 focus:border-pink-300 dark:focus:border-pink-500 outline-none transition-all font-medium text-gray-800 dark:text-gray-200"
                          placeholder={t('login.email')}
                        />
                      </div>

                      {view !== 'forgot' && (
                        <div>
                          <div className="relative">
                            <input 
                              type={showPassword ? "text" : "password"} 
                              required 
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              className="w-full pl-5 pr-12 py-4 bg-gray-50 dark:bg-slate-800 border-2 border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-4 focus:ring-pink-100 dark:focus:ring-pink-900/30 focus:border-pink-300 dark:focus:border-pink-500 outline-none transition-all font-medium text-gray-800 dark:text-gray-200"
                              placeholder={t('login.password')}
                            />
                            <button 
                              type="button" 
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-1"
                            >
                              <SafeIcon name={showPassword ? "EyeOff" : "Eye"} className="w-5 h-5" />
                            </button>
                          </div>
                          
                          <div className="flex justify-between items-center mt-2 px-2">
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-bold">
                              {t('login.passwordHint')}
                            </p>
                            {view === 'login' && (
                              <button 
                                type="button" 
                                onClick={() => switchView('forgot')}
                                className="text-xs font-bold text-pink-500 hover:text-pink-600 dark:hover:text-pink-400 transition-colors"
                              >
                                {t('login.forgotPassword')}
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {error && (
                        <div className="text-red-500 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-xl font-bold text-center border border-red-100 dark:border-red-900/50">
                          {error}
                        </div>
                      )}

                      {successMsg && (
                        <div className="text-green-600 dark:text-green-400 text-sm bg-green-50 dark:bg-green-900/20 p-3 rounded-xl font-bold text-center border border-green-100 dark:border-green-900/50">
                          {successMsg}
                        </div>
                      )}

                      <button 
                        type="submit" 
                        disabled={isLoading}
                        className="w-full py-4 bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-2xl font-bold shadow-lg active:scale-95 transition-all flex justify-center items-center gap-2 mt-2 disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {isLoading ? <SafeIcon name="Loader" className="animate-spin w-5 h-5" /> : null}
                        {isLoading ? t('common.processing') : (
                          view === 'forgot' ? t('login.sendResetBtn') :
                          view === 'login' ? t('login.loginBtn') : t('login.registerBtn')
                        )}
                      </button>
                    </form>
                  ) : (
                    <div className="text-center font-bold text-gray-500 bg-gray-50 dark:bg-slate-800 p-4 rounded-2xl">
                      Email login is currently disabled.
                    </div>
                  )}

                  {providers.email && (
                    <div className="mt-8 flex flex-col gap-4 items-center">
                      {view === 'forgot' ? (
                        <button 
                          onClick={() => switchView('login')}
                          className="text-sm font-bold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors flex items-center gap-1"
                        >
                          <SafeIcon name="ArrowLeft" className="w-4 h-4" />
                          {t('login.backToLogin')}
                        </button>
                      ) : (
                        <button 
                          onClick={() => switchView(view === 'login' ? 'register' : 'login')}
                          className="text-sm font-bold text-pink-500 hover:text-pink-600 dark:hover:text-pink-400 transition-colors"
                        >
                          {view === 'login' ? t('login.toggleToRegister') : t('login.toggleToLogin')}
                        </button>
                      )}

                      {view !== 'forgot' && (
                        <>
                          <div className="w-full flex items-center gap-3">
                            <div className="h-px bg-gray-200 dark:bg-slate-700 flex-1"></div>
                            <span className="text-xs text-gray-400 dark:text-gray-500 font-bold">{t('login.or')}</span>
                            <div className="h-px bg-gray-200 dark:bg-slate-700 flex-1"></div>
                          </div>

                          <Link 
                            to="/" 
                            className="text-sm font-bold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 w-full py-4 rounded-2xl text-center transition-colors shadow-sm"
                          >
                            {t('login.guestBtn')}
                          </Link>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>

      <SocialFooter onOpenSponsor={() => setIsSponsorOpen(true)} />
      <SponsorModal isOpen={isSponsorOpen} onClose={() => setIsSponsorOpen(false)} />
    </div>
  );
}